import {
  useGetPaymentInfoForFamilyMemberPhoenix,
  useGetPaymentProcessor,
  usePaymentInfoByResidentIdAsFamilyMember,
  useResidentBalanceInfo,
} from '@pflegenavi/frontend/api-nursing-home';
import { Stack, Typography } from '@mui/material';
import { PaymentInitiatedBy, PaymentSchedule } from '@pflegenavi/shared/api';
import {
  useFormatDate,
  useFormatting,
} from '@pflegenavi/frontend/localization';
import { useTranslation } from 'react-i18next';
import { addDays } from 'date-fns';
import { getDiffInDaysUntilDayOfMonth } from '@pflegenavi/shared/utils';
import { YEAR_MONTH_SHORTDAY_FORMAT } from '@pflegenavi/shared/constants';
import { useFMContext } from '@pflegenavi/frontend/family-member-context';
import { LoadingContainer } from '@pflegenavi/web-components';
import { useInitializeFM } from '../../hooks/useInitializeFM';

import type { JSX } from 'react';

type PaymentInitiationInfoViewProps =
  | InitiatedMonthly
  | InitiatedThreshold
  | NotInitiatedAutomatically;

interface NotInitiatedAutomatically {
  initiatedBy: PaymentInitiatedBy.FamilyMember | PaymentInitiatedBy.Disabled;
}

interface InitiatedManually {
  initiatedBy: PaymentInitiatedBy.FamilyMember;
  maxAmount: number;
  minimumPaymentAmount: number;
}

interface InitiatedMonthly {
  initiatedBy: PaymentInitiatedBy.System;
  schedule: PaymentSchedule.Monthly;
  dayOfMonth: number;
  maxAmount: number;
  topUpAmount: number;
  minimumPaymentAmount: number;
  paymentProcessor?: 'stripe' | 'mangopay';
  /** MangoPay: Fixed monthly payment amount */
  payinAmount?: number;
  /** MangoPay: Skip payment if wallet balance exceeds this */
  maxWalletAmount?: number;
}

interface InitiatedThreshold {
  initiatedBy: PaymentInitiatedBy.System;
  schedule: PaymentSchedule.Threshold;
  threshold: number;
  topUpAmount: number;
  paymentProcessor?: string;
}

export const PaymentInitiationInfoView = (
  props: PaymentInitiationInfoViewProps
): JSX.Element | null => {
  if (props.initiatedBy !== PaymentInitiatedBy.System) {
    return null;
  }

  if (props.schedule === PaymentSchedule.Monthly) {
    return <PaymentInitiationMonthly {...props} />;
  }
  return <PaymentInitiationThreshold {...props} />;
};

const PaymentInitiationManual = (props: InitiatedManually): JSX.Element => {
  const { t } = useTranslation();
  const { fCurrencyCents } = useFormatting();

  return (
    <Stack
      sx={{
        gap: 1,
      }}
    >
      <Typography variant="subtitle2">
        {t('mobile.family-member.direct-debit-manual-info', {
          maxAmount: fCurrencyCents(props.maxAmount),
          minimumPaymentAmount: fCurrencyCents(props.minimumPaymentAmount),
        })}
      </Typography>
    </Stack>
  );
};

const PaymentInitiationMonthly = (props: InitiatedMonthly) => {
  const fDate = useFormatDate();
  const { t } = useTranslation();
  const { fCurrencyCents } = useFormatting();
  const isMangoPay = props.paymentProcessor === 'mangopay';

  return (
    <Stack
      sx={{
        gap: 1,
      }}
    >
      <Stack>
        <Typography variant="body1">
          {t('resident.form.payment-type.payment-day')}:{' '}
          <Typography
            variant="body1"
            sx={{
              fontWeight: 700,
              display: 'inline-flex',
            }}
          >
            {props.dayOfMonth}.{' '}
            {t('resident.form.payment-type.payment-day-of-month')}
          </Typography>
        </Typography>
        <Typography variant="body1">
          {t(
            'resident.form.payment-type.payment-schedule-next-payment-short-label'
          )}
          :{' '}
          <Typography
            variant="body1"
            sx={{
              fontWeight: 700,
              display: 'inline-flex',
            }}
          >
            {fDate(
              addDays(
                new Date(),
                getDiffInDaysUntilDayOfMonth(new Date(), props.dayOfMonth)
              ),
              YEAR_MONTH_SHORTDAY_FORMAT
            )}
          </Typography>
        </Typography>
        {/* MangoPay: Show payinAmount and maxWalletAmount */}
        {isMangoPay &&
          props.payinAmount != null &&
          props.maxWalletAmount != null && (
            <>
              <Typography variant="body1">
                {t('resident.form.payment-type.fixed-monthly-amount')}:{' '}
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 700,
                    display: 'inline-flex',
                  }}
                >
                  {fCurrencyCents(props.payinAmount)}
                </Typography>
              </Typography>
              <Typography variant="body1">
                {t('resident.form.payment-type.maximum-amount')}:{' '}
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 700,
                    display: 'inline-flex',
                  }}
                >
                  {fCurrencyCents(props.maxWalletAmount)}
                </Typography>
              </Typography>
            </>
          )}
        {/* Stripe or fallback: Show topUpAmount and maxAmount */}
        {!isMangoPay && (
          <>
            <Typography variant="body1">
              {t('resident.top-up-thresholds.top-up-amount')}:{' '}
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 700,
                  display: 'inline-flex',
                }}
              >
                {fCurrencyCents(props.topUpAmount)}
              </Typography>
            </Typography>
            <Typography variant="body1">
              {t('resident.form.payment-type.max-amount')}:{' '}
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 700,
                  display: 'inline-flex',
                }}
              >
                {fCurrencyCents(props.maxAmount)}
              </Typography>
            </Typography>
          </>
        )}
      </Stack>
      {isMangoPay ? (
        <>
          {props.payinAmount != null && props.maxWalletAmount != null && (
            <Typography variant="subtitle2">
              {t('mobile.family-member.direct-debit-monthly-info-mangopay', {
                dayOfMonth: props.dayOfMonth,
                payinAmount: fCurrencyCents(props.payinAmount),
                maxWalletAmount: fCurrencyCents(props.maxWalletAmount),
              })}
            </Typography>
          )}
          <Typography variant="subtitle2">
            {t(
              'mobile.family-member.direct-debit-monthly-info-mangopay-nursing-home-changes'
            )}
          </Typography>
        </>
      ) : (
        <>
          <Typography variant="subtitle2">
            {t('mobile.family-member.direct-debit-monthly-info', {
              dayOfMonth: props.dayOfMonth,
              topUpAmount: fCurrencyCents(props.topUpAmount),
              maxAmount: fCurrencyCents(props.maxAmount),
              minimumPaymentAmount: fCurrencyCents(props.minimumPaymentAmount),
            })}
          </Typography>
          <Typography variant="subtitle2">
            {t(
              'mobile.family-member.direct-debit-monthly-info-nursing-home-changes'
            )}
          </Typography>
        </>
      )}
      {isMangoPay ? (
        <Typography variant="subtitle2">
          {t('mobile.family-member.direct-debit-monthly-notification-mangopay')}
        </Typography>
      ) : (
        <Typography variant="subtitle2">
          {t('mobile.family-member.direct-debit-upcoming-notification-info')}{' '}
          {t('mobile.family-member.direct-debit-updates')}
        </Typography>
      )}
    </Stack>
  );
};

const PaymentInitiationThreshold = (props: InitiatedThreshold) => {
  const { fCurrencyCents } = useFormatting();
  const { t } = useTranslation();
  return (
    <Stack
      sx={{
        gap: 1,
      }}
    >
      <Stack>
        <Typography variant="body1">
          {t('resident.top-up-thresholds.top-up-amount')}:{' '}
          <Typography
            variant="body1"
            sx={{
              fontWeight: 700,
              display: 'inline-flex',
            }}
          >
            {fCurrencyCents(props.topUpAmount)}
          </Typography>
        </Typography>
        <Typography variant="body1">
          {t('resident.top-up-thresholds.threshold')}:{' '}
          <Typography
            variant="body1"
            sx={{
              fontWeight: 700,
              display: 'inline-flex',
            }}
          >
            {fCurrencyCents(props.threshold)}
          </Typography>
        </Typography>
      </Stack>
      <Typography variant="subtitle2">
        {t('mobile.family-member.direct-debit-threshold-info', {
          topUpAmount: fCurrencyCents(props.topUpAmount),
          threshold: fCurrencyCents(props.threshold),
        })}
      </Typography>
      <Typography variant="subtitle2">
        {t(
          'mobile.family-member.direct-debit-threshold-info-nursing-home-changes'
        )}
      </Typography>
      {props.paymentProcessor === 'mangopay' && (
        <>
          <Typography variant="subtitle2">
            {t('mobile.family-member.direct-debit-info-handled-by-mangopay')}
          </Typography>
        </>
      )}
      <Typography variant="subtitle2">
        {t('mobile.family-member.direct-debit-upcoming-notification-info')}{' '}
        {t('mobile.family-member.direct-debit-updates')}
      </Typography>
    </Stack>
  );
};

export const PaymentInitiationInfo = (): JSX.Element | null => {
  const { selectedResident } = useInitializeFM();

  if (!selectedResident) {
    return <LoadingContainer />;
  }
  if (selectedResident?.tenantPaymentProcessor === 'stripe') {
    return <PaymentInitiationInfoStripe />;
  }
  return <PaymentInitiationInfoPhoenix />;
};

export const PaymentInitiationInfoPhoenix = (): JSX.Element | null => {
  const { selectedResident } = useInitializeFM();
  const { data, isLoading } = useResidentBalanceInfo({
    residentId: selectedResident?.residentId,
  });

  const { data: paymentInfo, isPending: isPaymentInfoLoading } =
    useGetPaymentInfoForFamilyMemberPhoenix(
      selectedResident?.residentId ?? '',
      selectedResident?.tenant ?? ''
    );

  const paymentProcessor = useGetPaymentProcessor();

  if (isLoading || isPaymentInfoLoading || !data || !paymentInfo) {
    return <LoadingContainer />;
  }

  const initiatedBy = paymentInfo.payment_settings
    .payment_initiation as PaymentInitiatedBy;
  const schedule = paymentInfo.payment_settings
    .payment_schedule as PaymentSchedule;
  const scheduleMaxAmount =
    paymentInfo.payment_settings.payment_schedule_max_amount;
  const dayOfMonth = paymentInfo.payment_settings.payment_schedule_day;

  // If FM initiated payments are enabled and the user wants to set up direct debit,
  // they will change to payment initiated by system with threshold schedule. We need to
  // overwrite the paymentInfo here to display the correct information.
  if (initiatedBy === PaymentInitiatedBy.FamilyMember) {
    return (
      // We don't need to consider the scheduleMaxAmount here, since it is only relevant for automated payments. For manual payments, FMs should be able to choose the amount they want within the bounds.
      <PaymentInitiationManual
        initiatedBy={initiatedBy}
        maxAmount={data.nursingHomeConfiguration.maxPaymentAmount}
        minimumPaymentAmount={data.nursingHomeConfiguration.minPaymentAmount}
      />
    );
  }

  if (initiatedBy === PaymentInitiatedBy.Employee) {
    throw new Error('Employee initiated payments are not supported');
  }

  if (initiatedBy === PaymentInitiatedBy.System) {
    if (schedule === PaymentSchedule.Monthly) {
      const maxAmount = !scheduleMaxAmount
        ? data.nursingHomeConfiguration.maxPaymentAmount
        : scheduleMaxAmount;
      return (
        <PaymentInitiationInfoView
          initiatedBy={initiatedBy}
          schedule={schedule}
          dayOfMonth={dayOfMonth!}
          maxAmount={maxAmount}
          topUpAmount={Math.round(data.residentConfiguration.topUpAmount * 100)}
          minimumPaymentAmount={data.nursingHomeConfiguration.minPaymentAmount}
          paymentProcessor={paymentProcessor}
          payinAmount={
            paymentInfo.payment_settings.payment_schedule_payin_amount ??
            undefined
          }
          maxWalletAmount={
            paymentInfo.payment_settings.payment_schedule_max_wallet_amount ??
            undefined
          }
        />
      );
    } else if (schedule === PaymentSchedule.Threshold) {
      return (
        <PaymentInitiationInfoView
          initiatedBy={initiatedBy}
          schedule={schedule}
          threshold={Math.round(data.residentConfiguration.threshold * 100)}
          topUpAmount={Math.round(data.residentConfiguration.topUpAmount * 100)}
          paymentProcessor={paymentProcessor}
        />
      );
    }
  } else {
    return <PaymentInitiationInfoView initiatedBy={initiatedBy} />;
  }

  return null;
};

export const PaymentInitiationInfoStripe = (): JSX.Element | null => {
  const { selectedResidentId } = useFMContext();
  const { data, isLoading } = useResidentBalanceInfo({
    residentId: selectedResidentId,
  });

  const { data: paymentInfo, isPending: isPaymentInfoLoading } =
    usePaymentInfoByResidentIdAsFamilyMember(selectedResidentId);

  const paymentProcessor = useGetPaymentProcessor();

  if (isLoading || isPaymentInfoLoading || !data || !paymentInfo) {
    return <LoadingContainer />;
  }

  // If FM initiated payments are enabled and the user wants to set up direct debit,
  // they will change to payment initiated by system with threshold schedule. We need to
  // overwrite the paymentInfo here to display the correct information.
  if (paymentInfo.initiatedBy === PaymentInitiatedBy.FamilyMember) {
    // @ts-expect-error // we are overwriting this property
    paymentInfo.initiatedBy = PaymentInitiatedBy.System;
    // @ts-expect-error // we are overwriting this property
    paymentInfo.schedule = { schedule: PaymentSchedule.Threshold };
  }

  if (paymentInfo.initiatedBy === PaymentInitiatedBy.System) {
    if (paymentInfo.schedule?.schedule === PaymentSchedule.Monthly) {
      const maxAmount = !paymentInfo.schedule.maxAmount
        ? data.nursingHomeConfiguration.maxPaymentAmount
        : paymentInfo.schedule.maxAmount;
      return (
        <PaymentInitiationInfoView
          initiatedBy={paymentInfo.initiatedBy}
          schedule={paymentInfo.schedule.schedule}
          dayOfMonth={paymentInfo.schedule.dayOfMonth!}
          maxAmount={maxAmount}
          topUpAmount={Math.round(data.residentConfiguration.topUpAmount * 100)}
          minimumPaymentAmount={data.nursingHomeConfiguration.minPaymentAmount}
        />
      );
    } else if (paymentInfo.schedule?.schedule === PaymentSchedule.Threshold) {
      return (
        <PaymentInitiationInfoView
          initiatedBy={paymentInfo.initiatedBy}
          schedule={paymentInfo.schedule!.schedule}
          threshold={Math.round(data.residentConfiguration.threshold * 100)}
          topUpAmount={Math.round(data.residentConfiguration.topUpAmount * 100)}
          paymentProcessor={paymentProcessor}
        />
      );
    }
  } else {
    return <PaymentInitiationInfoView initiatedBy={paymentInfo?.initiatedBy} />;
  }

  return null;
};
